<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type BacktestParams, type BacktestConfigStore, type WalkForwardResultData } from '../../api/client'
import { useAuth } from '../../composables/useAuth'
import WalkForwardChart from '../WalkForwardChart.vue'
import OptimizerResultsTable from '../OptimizerResultsTable.vue'
import CpcvPathsChart from '../CpcvPathsChart.vue'
import type { FundBacktestConfig } from './fundBacktestPanels'

// Option C (2026-10-11): the one step-aside fund backtest panel. Replaces
// the copy-pasted GoldBacktestPanel.vue / GgeBacktestPanel.vue; every
// per-fund difference (asset id, title, default params, knob section, stack
// preview, copy) comes from `config` (FUND_PAGES[slug].backtestPanel — see
// fundBacktestPanels.ts). Rendered DOM, testids and styling are those of the
// two original panels.
const props = defineProps<{ config: FundBacktestConfig }>()
const cfg = props.config
const assetId = cfg.assetId

const { isSuperUser } = useAuth()

// ── Fund-only knob surface ───────────────────────────────────────────
// All BGA-specific knobs (K_t, IMR, NoHold, VIX-mom gate, earnings, news
// sentiment, FVA, OTP hedge/overlay legs, staleness replay) are OMITTED —
// they are inert for these step-aside fund profiles. Presets round-trip
// those fields verbatim via the defaultParams merge, so cross-fund preset
// compatibility on the wire is preserved even though this panel never
// surfaces controls for them. The fund's own knobs are config.knobs.
const defaultParams: BacktestParams = cfg.defaultParams
const params = ref<BacktestParams>({ ...defaultParams })

const validationMode = ref<'none' | 'walk-forward' | 'cpcv'>('walk-forward')
// 2026-09-15: default WF submits type "walk-forward-quick" (24-combo grid); the
// "Full grid" checkbox opts into the exhaustive 240-combo run — that one takes
// ~25h on gold's 16y window and produced a 42-fold runaway.
// See docs/analysis/2026-09-15-walk-forward-runaway-fix.md.
const wfFullGrid = ref(false)
const trainDays = ref(250)
const testDays = ref(60)
const cpcvN = ref(6)
const cpcvK = ref(2)
const cpcvEmbargoDays = ref(5)
const tpeTrialBudget = ref(200)
const fitnessPreset = ref<'sharpe_only' | 'balanced' | 'risk_adjusted'>('sharpe_only')
const showAdvancedRuns = ref(false)

const configs = ref<BacktestConfigStore | null>(null)
const selectedConfig = ref('')
const activeJobId = ref<string | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const liveAppliedBanner = ref('')

// Config save/rename UI state — same inline-input flow as BacktestPanel.vue
// (deliberately NOT window.prompt/confirm: blocking dialogs freeze automation
// and diverge from the BGA/JH panel behaviour).
const showSaveInput = ref(false)
const saveConfigName = ref('')
const showRenameInput = ref(false)
const renameConfigName = ref('')

// System-locked presets (test fixtures) are hidden from the dropdown and
// the Save/Delete buttons are disabled while one is selected. See
// BacktestConfigService.PresetLockedException / paolo 2026-09-19.
const presetNames = computed(() => {
  const all = configs.value?.configs ?? {}
  return Object.entries(all)
    .filter(([name, e]) => !e.system_locked || name === selectedConfig.value)
    .map(([name]) => name)
})
const selectedIsLocked = computed<boolean>(() =>
  !!(selectedConfig.value && configs.value?.configs?.[selectedConfig.value]?.system_locked))
// 2026-09-15 step-aside dead-zone guard (paolo Q2). Mirrors
// BacktestParams.ValidateStepAsideBand() server-side.
const stepAsideBandError = computed<string | null>(() => {
  const sell = params.value.stepAsideThresholdBps ?? 0
  const buy = params.value.stepAsideBuyThresholdBps
  // Null = legacy one-sided rule (no dead zone) — always valid.
  if (buy != null && buy < sell)
    return `Futures rise to trigger BUY (${buy}bps) must be >= Futures drop to trigger SELL (${sell}bps) — otherwise the dead-zone rule flips into an unstable oscillator around the crossover.`
  return null
})

// ---- Formatting helpers ----
// Borrowed verbatim from BacktestPanel.vue — cagrPct/maxDrawdownPct
// are fractions (0.52 == 52%), not already-scaled percentages.
function fmtPct(val: number | null | undefined): string {
  if (val == null) return '--'
  return (val * 100).toFixed(1) + '%'
}
function fmtGbp(val: number | null | undefined, digits = 0): string {
  if (val == null) return '--'
  return '£' + val.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}
function fmtSharpe(val: number | null | undefined): string {
  if (val == null) return '--'
  return val.toFixed(2)
}
function valueClass(val: number | null | undefined, threshold = 0): string {
  if (val == null) return ''
  return val > threshold ? 'positive' : val < threshold ? 'negative' : ''
}
function decayClass(val: number | null | undefined): string {
  if (val == null) return ''
  const absVal = Math.abs(val)
  if (absVal < 0.50) return 'decay-warn'
  return 'decay-bad'
}

// ---- Recent-jobs list + active-job polling/hydration ----
const recentJobs = ref<any[]>([])
const activeJob = ref<any>(null)
// True while a job is queued/running server-side. Used to disable Run buttons
// so a fast double-click can't launch two concurrent jobs. See queuedSentinel
// below for how the sub-second race between HTTP response and first poll is
// closed (2026-08-23 UX report).
const isJobRunning = computed(() =>
  activeJob.value?.status === 'queued' || activeJob.value?.status === 'running')
function queuedSentinel(jobId: string) {
  return { jobId, status: 'queued' }
}
const currentResult = computed(() => activeJob.value?.result ?? null)
const currentJobType = computed(() => activeJob.value?.type ?? null)
const wfResult = computed<WalkForwardResultData | null>(() => activeJob.value?.walkForwardResult ?? null)
const wfOptimizeResult = computed<WalkForwardResultData | null>(() => activeJob.value?.walkForwardResult ?? null)
const tpeResult = computed(() => activeJob.value?.tpeResult ?? null)
let pollTimer: ReturnType<typeof setInterval> | null = null

// ---- Run-type labelling + previews (parity with BacktestPanel.vue) ----
const runButtonLabel = computed(() => {
  if (loading.value) return 'Running…'
  if (validationMode.value === 'cpcv') return 'Validate with CPCV'
  if (validationMode.value === 'walk-forward') return 'Run Walk-Forward'
  return 'Run Backtest'
})

function binomial(n: number, k: number): number {
  if (k < 0 || k > n) return 0
  let r = 1
  for (let i = 0; i < k; i++) r = (r * (n - i)) / (i + 1)
  return Math.round(r)
}

const runPreview = computed(() => {
  if (validationMode.value === 'cpcv') {
    const paths = binomial(cpcvN.value, cpcvK.value)
    return `CPCV: N=${cpcvN.value}, k=${cpcvK.value} → ${paths} paths, embargo ${cpcvEmbargoDays.value}d`
  }
  if (validationMode.value === 'walk-forward') {
    return `Walk-Forward: train ${trainDays.value}d / test ${testDays.value}d · ${fitnessPreset.value}`
  }
  return `Single run · ${fitnessPreset.value}`
})

const runStackPreview = computed(() => cfg.stackLabel(params.value))

const runTypeLabel = computed(() => {
  const t = currentJobType.value
  if (t === 'cpcv') return 'CPCV'
  if (t === 'walk-forward') return 'Walk-Forward'
  if (t === 'full-optimize') return 'Full Optimize (TPE)'
  if (t === 'single') return 'Single Run'
  return t ?? ''
})

const runSummaryMeta = computed(() => {
  const job = activeJob.value
  if (!job) return ''
  const parts: string[] = []
  const grid = job.walkForwardResult?.fixedGridResult ?? job.optimizeResult
  if (grid?.totalCombos) parts.push(`${grid.totalCombos} combos`)
  if (job.walkForwardResult?.folds?.length) parts.push(`${job.walkForwardResult.folds.length} folds`)
  const elapsedMs = grid?.elapsedMs ?? job.cpcvResult?.elapsedMs ?? 0
  if (elapsedMs) parts.push(`${(elapsedMs / 1000).toFixed(0)}s`)
  if (job.completedAt) parts.push(new Date(job.completedAt).toISOString().slice(0, 16).replace('T', ' ') + ' UTC')
  return parts.join(' | ')
})

// ---- Form-vs-result / form-vs-preset guards (parity with BacktestPanel) ----
// Same defect class the BGA panel guards against since 2026-05-19
// (databento_v3 got sweep-form params silently saved under a result name).
function formDiffsFromActiveJob(): string[] {
  const job = activeJob.value
  if (!job?.parameters) return []
  const expected: any = { ...defaultParams, ...job.parameters }
  const actual: any = { ...params.value }
  const out: string[] = []
  for (const k of Object.keys(expected)) {
    const a = JSON.stringify(actual[k] ?? null)
    const b = JSON.stringify(expected[k] ?? null)
    if (a !== b) out.push(k)
  }
  return out
}

const canSaveFromResults = computed(() => {
  if (!activeJob.value?.parameters) return true  // pre-job edits — save anything
  return formDiffsFromActiveJob().length === 0
})

const saveFromResultsLabel = computed(() => {
  if (!activeJob.value) return 'Save as Config'
  const diffs = formDiffsFromActiveJob()
  if (diffs.length > 0) {
    const sample = diffs.slice(0, 3).join(', ') + (diffs.length > 3 ? `, +${diffs.length - 3} more` : '')
    return `Form differs from scored result (${sample}) — click Load on the row you want, then Save`
  }
  return 'Save as Config'
})

function formDiffsFromSelected(): string[] {
  const sel = selectedConfig.value
  if (!sel || !configs.value) return []
  const entry = configs.value.configs[sel]
  if (!entry?.params) return []
  // loadPreset spreads { ...defaultParams, ...p } with no further
  // normalisation, so the expected side is the same symmetric merge.
  const expected: any = { ...defaultParams, ...entry.params }
  const actual: any = { ...params.value }
  const out: string[] = []
  for (const k of Object.keys(expected)) {
    const a = JSON.stringify(actual[k] ?? null)
    const b = JSON.stringify(expected[k] ?? null)
    if (a !== b) out.push(k)
  }
  return out
}

const canApplyToLive = computed(() => {
  if (!selectedConfig.value || !configs.value) return false
  if (!configs.value.configs[selectedConfig.value]) return false
  return formDiffsFromSelected().length === 0
})

const applyToLiveLabel = computed(() => {
  const sel = selectedConfig.value
  if (!sel) return 'Apply to Live — select a preset'
  const diffs = formDiffsFromSelected()
  if (diffs.length > 0) {
    const sample = diffs.slice(0, 3).join(', ') + (diffs.length > 3 ? `, +${diffs.length - 3} more` : '')
    return `Form differs from '${sel}' (${sample}) — Save first`
  }
  return `Apply '${sel}' to Live`
})

// ---- Equity curve chart ----
// Verbatim copy of BacktestPanel.vue's `equityCurvePoints` computed (SVG
// viewport/scaleY/polyline-building), trimmed to strategy + B&H only. The
// unhedged / BGA-contribution lines that BGA also plots are OTP hedge/overlay
// concepts, which are inert for these funds and never populate on their results.
const CHART_POINTS_CAP = 200_000

const equityCurvePoints = computed(() => {
  const r = currentResult.value
  if (!r?.equityCurve || r.equityCurve.length < 2) return null

  // Defensive: skip rendering if the dataset is pathologically large
  if (r.equityCurve.length > CHART_POINTS_CAP) {
    console.warn(`equityCurvePoints: skipping render, ${r.equityCurve.length} points exceeds cap ${CHART_POINTS_CAP}`)
    return null
  }

  const pts = r.equityCurve
  const bhPts = r.buyAndHoldCurve ?? []
  const W = 500, H = 160, PAD = 10, LEGEND_W = 75

  // Convert NPV to percentage return from start
  const startNpv = pts[0].npv || 1
  const toRet = (npv: number) => ((npv / startNpv) - 1) * 100

  const stratRets = pts.map((p: any) => toRet(p.npv))
  const bhStartNpv = bhPts.length > 0 ? bhPts[0].npv || 1 : 1
  const bhRets = bhPts.map((p: any) => ((p.npv / bhStartNpv) - 1) * 100)

  // Scale Y to encompass both curves in % space
  // Defensive: filter out non-finite values — a blown-up backtest can produce
  // Infinity/NaN NPVs that would make maxY infinite and trigger an infinite
  // grid-line loop below (→ RangeError on join).
  const allRets = [...stratRets, ...bhRets].filter(Number.isFinite)
  if (allRets.length === 0) {
    console.warn('equityCurvePoints: skipping render, no finite return values')
    return null
  }
  const minY = Math.min(...allRets, 0) - 5
  const maxY = Math.max(...allRets) * 1.1
  const rangeY = maxY - minY || 1
  // Hard safety cap: a legitimate backtest never spans more than ~1,000,000% range.
  // Cap raised from 10_000 (BGA default) — gold's/GGE's full-history compounding hits ~200k% return.
  // If it does exceed this, something is broken upstream — skip rather than crash.
  if (!Number.isFinite(rangeY) || rangeY > 1_000_000) {
    console.warn(`equityCurvePoints: skipping render, rangeY=${rangeY} outside sane bounds`)
    return null
  }

  const scaleX = (i: number, total: number) => (i / (total - 1)) * W
  const scaleY = (v: number) => H - PAD - ((v - minY) / rangeY) * (H - PAD * 2)

  // Strategy line (solid green)
  const polyline = stratRets.map((ret: number, i: number) => `${scaleX(i, pts.length).toFixed(1)},${scaleY(ret).toFixed(1)}`).join(' ')

  // B&H line (dashed grey)
  let bhPolyline = ''
  if (bhRets.length >= 2) {
    bhPolyline = bhRets.map((ret: number, i: number) => `${scaleX(i, bhPts.length).toFixed(1)},${scaleY(ret).toFixed(1)}`).join(' ')
  }

  // Zero line
  const zeroY = scaleY(0)

  // Grid lines at regular % intervals
  const gridLines: string[] = []
  const step = rangeY > 100 ? 50 : rangeY > 50 ? 25 : 10
  // Belt-and-braces cap: even with sane inputs, never emit more than 1000 grid lines.
  const maxGridLines = 1000
  let gridIter = 0
  for (let v = Math.ceil(minY / step) * step; v <= maxY && gridIter < maxGridLines; v += step, gridIter++) {
    const y = scaleY(v)
    gridLines.push(`<line x1="0" y1="${y}" x2="${W}" y2="${y}" stroke="#2a2a2a" stroke-width="0.5"/>`)
  }
  // Zero line slightly brighter
  gridLines.push(`<line x1="0" y1="${zeroY}" x2="${W}" y2="${zeroY}" stroke="#444" stroke-width="0.5"/>`)

  // Y axis labels (%)
  const fmtRetPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`
  const yLabels = [
    `<text x="${W + 4}" y="${scaleY(maxY) + 4}" fill="#555" font-size="7">${fmtRetPct(maxY)}</text>`,
    `<text x="${W + 4}" y="${zeroY + 3}" fill="#666" font-size="7">0%</text>`,
    ...(minY < -5 ? [`<text x="${W + 4}" y="${scaleY(minY) + 4}" fill="#555" font-size="7">${fmtRetPct(minY)}</text>`] : []),
  ]

  // X axis labels
  const startLabel = pts[0].date.substring(0, 7)
  const midIdx = Math.floor(pts.length / 2)
  const midLabel = pts[midIdx].date.substring(0, 7)
  const endLabel = pts[pts.length - 1].date.substring(0, 7)
  const xLabels = [
    `<text x="0" y="${H}" fill="#555" font-size="8">${startLabel}</text>`,
    `<text x="${W / 2 - 15}" y="${H}" fill="#555" font-size="8">${midLabel}</text>`,
    `<text x="${W - 35}" y="${H}" fill="#555" font-size="8">${endLabel}</text>`,
  ]

  // CAGR: use backend value for strategy (matches KPI card), compute for B&H
  const tradingDays = r.tradingDays || pts.length
  const calendarYears = tradingDays / 252
  const npvToCagr = (fromNpv: number, toNpv: number) => {
    if (calendarYears <= 0 || fromNpv <= 0) return 0
    return (Math.pow(toNpv / fromNpv, 1 / calendarYears) - 1) * 100
  }
  const fmtCagr = (cagr: number) => `${cagr >= 0 ? '+' : ''}${cagr.toFixed(1)}% pa`

  // Strategy CAGR: use backend's cagrPct (matches KPI card exactly)
  const stratCagr = (r.cagrPct ?? 0) * 100

  // B&H: compute from initial cash (50000) to final NPV
  const INITIAL_CASH = 50000
  const bhCagr = bhPts.length > 0 ? npvToCagr(INITIAL_CASH, bhPts[bhPts.length - 1].npv) : 0

  // Legend with CAGR at end of each line
  const stratFinalRet = stratRets[stratRets.length - 1]
  const stratEndY = scaleY(stratFinalRet)
  const bhFinalRet = bhRets.length > 0 ? bhRets[bhRets.length - 1] : 0
  const bhEndY = bhRets.length > 0 ? scaleY(bhFinalRet) : 0

  // Prevent legend labels from overlapping by spacing them
  const labelPositions: { y: number; label: string; color: string; bold: boolean }[] = [
    { y: stratEndY, label: `Strategy ${fmtCagr(stratCagr)}`, color: '#4caf50', bold: true },
  ]
  if (bhRets.length > 0) {
    labelPositions.push({ y: bhEndY, label: `Buy & Hold ${fmtCagr(bhCagr)}`, color: '#666', bold: false })
  }
  // Sort by Y position and space out if overlapping
  labelPositions.sort((a, b) => a.y - b.y)
  for (let i = 1; i < labelPositions.length; i++) {
    if (labelPositions[i].y - labelPositions[i - 1].y < 10) {
      labelPositions[i].y = labelPositions[i - 1].y + 10
    }
  }

  const legend = labelPositions.map(l =>
    `<text x="${W + 4}" y="${l.y + 3}" fill="${l.color}" font-size="7"${l.bold ? ' font-weight="bold"' : ''}>${l.label}</text>`
  )

  return {
    viewBox: `0 0 ${W + LEGEND_W} ${H + 5}`,
    grid: gridLines.join(''),
    polyline,
    bhPolyline,
    hasBh: bhRets.length >= 2,
    yLabels: yLabels.join(''),
    xLabels: xLabels.join(''),
    legend: legend.join(''),
  }
})

// ---- Walk-forward folds (parity with BacktestPanel.vue) ----
const currentFolds = computed(() => {
  if (!wfResult.value) return []
  const folds = wfResult.value.folds ?? []
  const foldResults = wfResult.value.refitFoldResults ?? wfResult.value.fixedFoldResults
  return folds.map((f, i) => ({
    ...f,
    result: foldResults?.[i] ?? null,
  }))
})

const hasFoldResults = computed(() => {
  const wf = wfResult.value
  if (!wf?.folds?.length) return false
  const results = wf.refitFoldResults ?? wf.fixedFoldResults
  return !!results && results.length > 0 && !!results[0]?.equityCurve?.length
})

// ---- CPCV distribution + per-path table helpers ----
// distributionRows: verbatim ordering/labelling from BacktestPanel.vue's
// distributionRows computed — capitalised, human-preferred order (Sharpe,
// Sortino, CAGR, Max DD, Profit Factor, Win Rate, then Fitness if present).
const distributionRows = computed(() => {
  const d = activeJob.value?.cpcvResult?.distribution
  if (!d) return {} as Record<string, any>
  const rows: Record<string, any> = {
    'Sharpe': d.sharpe,
    'Sortino': d.sortino,
    'CAGR': d.cagr,
    'Max DD': d.maxDrawdown,
    'Profit Factor': d.profitFactor,
    'Win Rate': d.winRate,
  }
  if (d.fitness) rows['Fitness'] = d.fitness
  return rows
})

// Verbatim copy of BacktestPanel.vue's stdColour/pathConsistencyColour —
// colour-codes stdDev dispersion and flags paths whose Sharpe falls outside
// one stdDev of the distribution mean.
function stdColour(std: number, mean: number): string {
  if (Math.abs(mean) < 1e-9) return '#888'
  const ratio = Math.abs(std / mean)
  if (ratio < 0.2) return '#4caf50'
  if (ratio < 0.5) return '#fbc02d'
  return '#f44336'
}
function pathConsistencyColour(pathSharpe: number): string {
  const d = activeJob.value?.cpcvResult?.distribution?.sharpe
  if (!d) return '#888'
  const distFromMean = Math.abs(pathSharpe - d.mean)
  return distFromMean <= d.stdDev ? '#4caf50' : '#fbc02d'
}

async function loadRecentJobs() {
  try {
    const jobs = await api.backtestJobs(assetId)
    recentJobs.value = (jobs ?? [])
      .filter((j: any) => j.assetId === assetId && j.status === 'completed')
      .sort((a: any, b: any) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime())
      .slice(0, 20)
  } catch (e: any) { /* silent — recent-jobs list is nice-to-have */ }
}

async function selectRecentJob(jobId: string) {
  if (!jobId) { activeJob.value = null; return }
  stopPolling()
  // Recent-jobs list entries already carry the full `result` payload (per
  // the BacktestJob wire shape), so prefer the cached entry over a redundant
  // round-trip; fall back to a direct fetch if it's somehow not cached.
  const cached = recentJobs.value.find((j: any) => j.jobId === jobId)
  if (cached) { activeJob.value = cached; return }
  try { activeJob.value = await api.backtestJob(jobId) }
  catch (e: any) { error.value = e?.message ?? 'failed to load job' }
}

function startPolling(jobId: string) {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const j = await api.backtestJob(jobId)
      activeJob.value = j
      if (j.status === 'completed' || j.status === 'failed') {
        stopPolling()
        loadRecentJobs()  // refresh dropdown to include this run
      }
    } catch { /* keep polling */ }
  }, 2000)
}
function stopPolling() { if (pollTimer) { clearInterval(pollTimer); pollTimer = null } }

async function loadConfigs() {
  try {
    configs.value = await api.backtestConfigs(assetId)
    if (configs.value?.active) {
      selectedConfig.value = configs.value.active
      loadPreset(configs.value.active)
    }
  } catch (e: any) {
    error.value = e?.message ?? 'failed to load configs'
  }
}

let loadPresetSeq = 0

// 2026-10-13: the Gold and GGE live producers never implement IMR (ImrModeLiveSupport), and this
// panel has no IMR control, so a preset's imr_mode could only ride along unseen into
// config:instrument_mr:{fund}. Every load forces it off. A preset saved with 'filter' then shows
// "form differs (imrMode)" until it is re-run and saved, so the next Apply to Live writes mode off.
function withImrOff(p: BacktestParams): BacktestParams {
  return { ...p, imrMode: 'off' }
}

async function loadPreset(name: string) {
  const entry = configs.value?.configs?.[name]
  if (!entry?.params) return
  // Only the fund-relevant fields are ever surfaced as inputs below; any
  // other field on the preset (e.g. legacy BGA-only fields carried on an
  // old full-file save) still round-trips through the form state via this
  // merge, it's just never editable here.
  params.value = withImrOff({ ...defaultParams, ...(entry.params as any) })
  const seq = ++loadPresetSeq
  linkedJobMissing.value = false
  relinkError.value = null
  if (entry.source_job_id) {
    try {
      const job = await api.backtestJob(entry.source_job_id)
      if (seq !== loadPresetSeq) return
      if (job?.status === 'completed') {
        activeJob.value = job
        activeJobId.value = job.jobId
      } else {
        activeJob.value = null
        activeJobId.value = null
        linkedJobMissing.value = true
      }
    } catch {
      if (seq !== loadPresetSeq) return
      activeJob.value = null
      activeJobId.value = null
      linkedJobMissing.value = true
    }
  } else {
    activeJob.value = null
    activeJobId.value = null
    linkedJobMissing.value = true
  }
}

// Heal a preset whose linked backtest_results/{jobId}.json was purged (or
// was never generated for a programmatic save with source_job_id=null).
// See BacktestPanel.vue (BGA) for the same pattern + memory
// feedback_preset_needs_source_job_id_for_graph.
const linkedJobMissing = ref(false)
const relinking = ref(false)
const relinkError = ref<string | null>(null)

async function runAndLinkSelectedPreset() {
  if (!selectedConfig.value || !configs.value) return
  const entry = configs.value.configs[selectedConfig.value]
  if (!entry) return
  relinking.value = true
  relinkError.value = null
  try {
    const run = await api.backtestRun('single', entry.params, { assetId: assetId, fitnessPreset: 'sharpe_only' })
    const jobId = run.jobId
    let job: any = null
    const deadline = Date.now() + 5 * 60_000
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3000))
      job = await api.backtestJob(jobId)
      if (job?.status === 'completed' || job?.status === 'failed') break
    }
    if (!job || job.status !== 'completed') {
      throw new Error(job?.error || `Backtest ${job?.status ?? 'timed out'}`)
    }
    await api.backtestRelinkConfig(selectedConfig.value, jobId, assetId)
    await loadConfigs()
    await loadPreset(selectedConfig.value)
  } catch (err: unknown) {
    relinkError.value = err instanceof Error ? err.message : String(err)
  } finally {
    relinking.value = false
  }
}

// Copy a walk-forward fixed-grid row's exact params into the Backtest form
// (same "Load" affordance BacktestPanel.vue wires via loadTpeWinner/loadOptimizerRow —
// OptimizerResultsTable emits the full BacktestParams for the clicked row).
function loadOptimizerRow(p: BacktestParams) {
  params.value = withImrOff({ ...defaultParams, ...p })
}

// Which TPE group (if any) is currently backfilling a full-engine single run.
// See fallback branch in loadTpeWinner below — mirrors BacktestPanel.vue.
const tpeGroupBackfilling = ref<{ label: string } | null>(null)

// Same affordance as BacktestPanel.loadTpeWinner: load the group's winning
// params into the form and surface its authoritative fullResult metrics
// in the KPI cards (clearing walkForwardResult so cards re-derive).
async function loadTpeWinner(group: any) {
  const merged = withImrOff({ ...defaultParams, ...group.bestParams })
  params.value = merged
  selectedConfig.value = ''
  if (group.fullResult && activeJob.value) {
    activeJob.value = {
      ...activeJob.value,
      result: group.fullResult,
      walkForwardResult: undefined,
    }
    return
  }
  // Fallback (2026-09-18): TPE detail-pass runs full-engine only for the
  // top-10 groups. Beyond top 10, group.fullResult is null and Load used to
  // silently skip the result panel. Run a fresh single-mode backtest instead.
  if (tpeGroupBackfilling.value) return
  tpeGroupBackfilling.value = { label: group.label ?? '(unnamed)' }
  error.value = null
  try {
    const runResp = await api.backtestRun('single', merged, {
      assetId: assetId, fitnessPreset: 'sharpe_only',
    })
    const jobId = runResp.jobId
    const startedAt = Date.now()
    while (Date.now() - startedAt < 5 * 60 * 1000) {
      await new Promise(r => setTimeout(r, 3000))
      const job: any = await api.backtestJob(jobId)
      if (job?.status === 'completed') {
        if (activeJob.value) {
          activeJob.value = {
            ...activeJob.value,
            result: job.result,
            walkForwardResult: undefined,
            tpeResult: activeJob.value.tpeResult,
          } as any
        }
        return
      }
      if (job?.status === 'failed') {
        error.value = `Detail-pass backtest failed for group '${group.label ?? '(unnamed)'}': ${job.error ?? 'unknown error'}`
        return
      }
    }
    error.value = `Detail-pass backtest for group '${group.label ?? '(unnamed)'}' timed out after 5 min — check the Jobs list.`
  } catch (e: any) {
    error.value = `Detail-pass backtest failed for group '${group.label ?? '(unnamed)'}': ${e?.message ?? e}`
  } finally {
    tpeGroupBackfilling.value = null
  }
}

// Cancel the currently in-flight backtest via DELETE /api/backtest/jobs/{id}.
// Server-side only interrupts QUEUED jobs (see BacktestJobService.CancelJob);
// running-job interruption is deferred. The next poll settles the real state
// so we don't optimistically clear activeJob here.
async function cancelBacktest() {
  const id = activeJobId.value
  if (!id) return
  try {
    await api.backtestCancelJob(id)
  } catch (e: any) {
    error.value = e?.message ?? 'Failed to cancel backtest'
  }
}

async function runBacktest() {
  loading.value = true
  error.value = null
  // 2026-09-15: refuse buy < sell client-side too (server refuses too;
  // this saves a round trip and gives immediate feedback).
  if (stepAsideBandError.value) {
    error.value = stepAsideBandError.value
    loading.value = false
    return
  }
  try {
    const type =
      validationMode.value === 'walk-forward' ? (wfFullGrid.value ? 'walk-forward' : 'walk-forward-quick') :
      validationMode.value === 'cpcv' ? 'cpcv' : 'single'
    const res = await api.backtestRun(type, { ...params.value }, {
      assetId: assetId,
      fitnessPreset: fitnessPreset.value,
      trainDays: trainDays.value,
      testDays: testDays.value,
      cpcvN: cpcvN.value,
      cpcvK: cpcvK.value,
      cpcvEmbargoDays: cpcvEmbargoDays.value,
      tpeTrialBudget: tpeTrialBudget.value,
    })
    activeJobId.value = res.jobId
    activeJob.value = queuedSentinel(res.jobId)
    startPolling(res.jobId)
  } catch (e: any) {
    error.value = e?.message ?? 'run failed'
  } finally {
    loading.value = false
  }
}

// Full TPE optimize — same run type BacktestPanel exposes under "Advanced
// runs" (backend's full-optimize case is asset-agnostic; profile resolves
// via assetId).
async function runFullOptimize() {
  loading.value = true
  error.value = null
  try {
    const res = await api.backtestRun('full-optimize', { ...params.value }, {
      assetId: assetId,
      fitnessPreset: fitnessPreset.value,
      trainDays: trainDays.value,
      testDays: testDays.value,
      stepDays: 60,
      tpeTrialBudget: tpeTrialBudget.value,
    })
    activeJobId.value = res.jobId
    activeJob.value = queuedSentinel(res.jobId)
    startPolling(res.jobId)
  } catch (e: any) {
    error.value = e?.message ?? 'Failed to start full optimization'
  } finally {
    loading.value = false
  }
}

// ---- Config CRUD (parity with BacktestPanel.vue) ----
async function saveConfig(name: string) {
  if (!name) return
  try {
    try {
      await api.backtestSaveConfig(name, { ...params.value }, activeJob.value?.jobId, assetId)
    } catch (e: any) {
      // Overwrite guard — same confirm-then-retry flow as BacktestPanel
      // (2026-08-12 pinned-preset drift incident).
      if (!/already exists/i.test(e?.message ?? '')) throw e
      if (!window.confirm(
        `Preset '${name}' already exists. Overwrite it with the current parameters? `
        + `The previous values will be replaced (this is event-logged).`)) return
      await api.backtestSaveConfig(name, { ...params.value }, activeJob.value?.jobId, assetId, true)
    }
    await loadConfigs()
    selectedConfig.value = name
    showSaveInput.value = false
    saveConfigName.value = ''
    liveAppliedBanner.value = `Config saved: ${name}`
    setTimeout(() => { liveAppliedBanner.value = '' }, 5000)
  } catch (e: any) {
    error.value = e?.message ?? 'Failed to save config'
  }
}

// Results-panel save: guarded so a form state that doesn't match the
// displayed result can't be persisted under the result's name (same
// databento_v3-class guard as BacktestPanel).
async function saveConfigFromResults() {
  if (activeJob.value?.parameters) {
    const diffs = formDiffsFromActiveJob()
    if (diffs.length > 0) {
      const sample = diffs.slice(0, 5).join(', ') + (diffs.length > 5 ? `, +${diffs.length - 5} more` : '')
      error.value = `Form differs from the scored result on: ${sample}. `
        + `Click 'Load' on the result row you want to save, then Save.`
      return
    }
  }
  showSaveInput.value = true
  saveConfigName.value = selectedConfig.value || ''
}

async function renameConfig() {
  const oldName = selectedConfig.value
  const newName = renameConfigName.value?.trim()
  if (!oldName || !newName || oldName === newName) return
  try {
    const store = configs.value
    const existingEntry = store?.configs?.[oldName]
    if (existingEntry) {
      await api.backtestSaveConfig(newName, existingEntry.params, existingEntry.source_job_id ?? undefined, assetId)
      await api.backtestDeleteConfig(oldName, assetId)
      await loadConfigs()
      selectedConfig.value = newName
    }
    showRenameInput.value = false
    renameConfigName.value = ''
  } catch (e: any) {
    error.value = e?.message ?? 'Failed to rename config'
  }
}

async function deleteConfig(name: string) {
  if (!name) return
  try {
    await api.backtestDeleteConfig(name, assetId)
    await loadConfigs()
    if (selectedConfig.value === name) selectedConfig.value = ''
  } catch (e: any) {
    error.value = e?.message ?? 'Failed to delete config'
  }
}

// Guarded promote — same flow as BacktestPanel.applyToLiveTrading: the
// gate is the form-vs-preset diff (button disabled + explanatory label),
// not a blocking confirm dialog. The step-aside churn caveat lives in the
// static caption under the button.
async function applyToLive() {
  error.value = null
  liveAppliedBanner.value = ''
  const sel = selectedConfig.value
  if (!sel) {
    error.value = 'Select a preset from the dropdown before applying to live.'
    return
  }
  const diffs = formDiffsFromSelected()
  if (diffs.length > 0) {
    const sample = diffs.slice(0, 5).join(', ') + (diffs.length > 5 ? '…' : '')
    error.value = `Form has ${diffs.length} unsaved edit${diffs.length === 1 ? '' : 's'} vs '${sel}' (${sample}). `
      + `Save the form as a new preset first, or reselect '${sel}' from the dropdown to discard edits.`
    return
  }
  try {
    await api.backtestSetActive(sel, assetId)
    await loadConfigs()
    liveAppliedBanner.value = `'${sel}' applied to live`
    setTimeout(() => { liveAppliedBanner.value = '' }, 5000)
  } catch (e: any) {
    error.value = e?.message ?? 'apply failed'
  }
}

onMounted(() => {
  loadConfigs()
  loadRecentJobs()
})
onUnmounted(() => stopPolling())
</script>

<template>
  <div class="fund-backtest" :class="cfg.rootClass" :data-testid="`${assetId}-backtest-panel`">
    <h2>{{ cfg.title }}</h2>

    <div v-if="error" class="error">{{ error }}</div>
    <div v-if="liveAppliedBanner" class="live-applied-banner">{{ liveAppliedBanner }}</div>

    <div class="panel-grid">
    <div class="controls-column">

    <!-- Preset selector + config CRUD (parity with BacktestPanel Configs section) -->
    <section class="preset-section">
      <label>Preset:
        <select v-model="selectedConfig" @change="loadPreset(selectedConfig)">
          <option v-for="n in presetNames" :key="n" :value="n">{{ n }}{{ n === configs?.active ? ' ★' : '' }}</option>
        </select>
      </label>
      <div class="config-btn-row">
        <button class="btn-sm" data-testid="save-preset-btn" @click="showSaveInput = !showSaveInput"
                :disabled="selectedIsLocked"
                :title="selectedIsLocked ? 'System-locked preset (test fixture) — cannot be overwritten. Save under a different name.' : ''">Save</button>
        <button class="btn-sm" data-testid="rename-preset-btn" @click="showRenameInput = !showRenameInput" :disabled="!selectedConfig">Rename</button>
        <button class="btn-sm btn-danger" data-testid="delete-preset-btn" @click="deleteConfig(selectedConfig)"
                :disabled="!selectedConfig || selectedIsLocked"
                :title="selectedIsLocked ? 'System-locked preset (test fixture) — cannot be deleted.' : ''">Delete</button>
      </div>
      <div v-if="showSaveInput" class="config-save-row">
        <input v-model="saveConfigName" placeholder="Config name" data-testid="save-preset-name-input" @keyup.enter="saveConfig(saveConfigName)" />
        <button class="btn-sm" data-testid="save-preset-confirm-btn" @click="saveConfig(saveConfigName)">Save</button>
      </div>
      <div v-if="showRenameInput" class="config-save-row">
        <input v-model="renameConfigName" placeholder="New name" @keyup.enter="renameConfig" />
        <button class="btn-sm" @click="renameConfig">Rename</button>
      </div>
    </section>

    <!-- The fund's own knob sections (config.knobs) -->
    <component :is="cfg.knobs" :params="params" :band-error="stepAsideBandError" />

    <!-- Validation mode -->
    <section class="validation-section">
      <h3>Validation</h3>
      <label><input type="radio" value="none" v-model="validationMode" /> Single run</label>
      <label><input type="radio" value="walk-forward" v-model="validationMode" /> Walk-forward</label>
      <label><input type="radio" value="cpcv" v-model="validationMode" /> CPCV</label>

      <div v-if="validationMode === 'walk-forward'">
        <label>Train days: <input type="number" v-model.number="trainDays" /></label>
        <label>Test days: <input type="number" v-model.number="testDays" /></label>
        <label :title="cfg.fullGridTitle">
          <input type="checkbox" v-model="wfFullGrid" data-testid="wf-full-grid" />
          Full grid (slow: 240 combos, hours-to-a-day)
        </label>
      </div>
      <div v-if="validationMode === 'cpcv'">
        <label>N: <input type="number" v-model.number="cpcvN" /></label>
        <label>K: <input type="number" v-model.number="cpcvK" /></label>
        <label>Embargo days: <input type="number" v-model.number="cpcvEmbargoDays" /></label>
      </div>

      <label>Fitness preset:
        <select v-model="fitnessPreset">
          <option value="sharpe_only">sharpe_only</option>
          <option value="balanced">balanced</option>
          <option value="risk_adjusted">risk_adjusted</option>
        </select>
      </label>
    </section>

    <!-- Actions — dynamic primary run label + previews, parity with BacktestPanel -->
    <section class="actions-section">
      <div class="run-row">
        <button class="btn-run primary-run-btn" data-testid="run-button" @click="runBacktest" :disabled="loading || isJobRunning">
          {{ runButtonLabel }}
        </button>
        <button
          v-if="isJobRunning"
          class="btn-cancel"
          data-testid="cancel-button"
          @click="cancelBacktest"
          title="Cancels a queued backtest. If it has already started running, the cancel is a no-op (running-job interruption not yet wired).">
          Cancel
        </button>
      </div>
      <div class="run-preview" data-testid="run-preview">{{ runPreview }}</div>
      <div class="run-preview stack-preview" data-testid="run-stack-preview">{{ runStackPreview }}</div>

      <div class="advanced-toggle" data-testid="advanced-runs-toggle" @click="showAdvancedRuns = !showAdvancedRuns">
        {{ showAdvancedRuns ? '▾' : '▸' }} Advanced runs
      </div>
      <div v-if="showAdvancedRuns" class="advanced-runs">
        <button class="btn-run btn-tpe" data-testid="run-tpe-btn" @click="runFullOptimize" :disabled="loading || isJobRunning">
          {{ loading ? 'Running…' : `Full Optimize (TPE ${tpeTrialBudget})` }}
        </button>
        <input type="number" v-model.number="tpeTrialBudget" class="tpe-budget-input" min="10" max="5000" />
      </div>

      <button v-if="isSuperUser" class="btn-action btn-warn apply-btn" data-testid="apply-live-button"
              @click="applyToLive" :disabled="loading || !canApplyToLive" :title="applyToLiveLabel">
        {{ applyToLiveLabel }}
      </button>
      <div v-if="isSuperUser" class="apply-caveat">
        {{ cfg.applyCaveat }}
      </div>
    </section>

    <p v-if="activeJobId" class="job-id">Active job: {{ activeJobId }}</p>

    </div>
    <div class="results-column" data-testid="results-column">
      <div class="results-header">
        <label>Load previous run:
          <select data-testid="recent-jobs-select"
                  @change="selectRecentJob(($event.target as HTMLSelectElement).value)">
            <option value="">— select —</option>
            <option v-for="j in recentJobs" :key="j.jobId" :value="j.jobId">
              {{ j.completedAt?.slice(0, 16).replace('T', ' ') }} · {{ j.type }} · Sharpe {{ j.result?.sharpeRatio?.toFixed(2) ?? '—' }}
            </option>
          </select>
        </label>
        <span v-if="currentJobType" class="job-type-badge" data-testid="job-type-badge">{{ currentJobType }}</span>
      </div>

      <!-- Results action bar (parity with BacktestPanel) -->
      <div v-if="activeJob?.result || activeJob?.walkForwardResult || activeJob?.cpcvResult || activeJob?.tpeResult"
           class="results-action-bar" data-testid="results-action-bar">
        <button class="btn-action"
                @click="saveConfigFromResults"
                :disabled="loading || !canSaveFromResults"
                :title="saveFromResultsLabel"
                data-testid="save-config-btn">
          {{ canSaveFromResults ? 'Save as Config' : 'Save as Config (form ≠ result)' }}
        </button>
        <button v-if="isSuperUser" class="btn-action btn-warn"
                @click="applyToLive"
                :disabled="loading || !canApplyToLive"
                :title="applyToLiveLabel"
                data-testid="apply-to-live-btn">
          {{ applyToLiveLabel }}
        </button>
      </div>

      <!-- Run Summary header (parity with BacktestPanel) -->
      <div v-if="activeJob?.result || activeJob?.walkForwardResult || activeJob?.cpcvResult || activeJob?.tpeResult"
           class="run-summary" data-testid="run-summary">
        <div class="run-summary-top">
          <span class="run-type">{{ runTypeLabel }}</span>
          <span v-if="activeJob?.fitnessPreset" class="run-preset">— {{ activeJob.fitnessPreset }}</span>
        </div>
        <div class="run-summary-meta">{{ runSummaryMeta }}</div>
      </div>

      <!-- No results yet -->
      <div v-if="!activeJob && !loading" data-testid="no-results" class="no-results">
        <div>Run a backtest to see results, or load a previous run above.</div>
        <div v-if="selectedConfig && linkedJobMissing" style="margin-top: 12px;">
          <button
            class="btn-action"
            data-testid="relink-preset-button"
            :disabled="relinking"
            @click="runAndLinkSelectedPreset">
            {{ relinking ? 'Running…' : 'Run backtest to link' }}
          </button>
          <div v-if="relinkError" class="relink-error" data-testid="relink-error"
               style="color: var(--sw-danger, #c05); margin-top: 8px; font-size: 12px;">
            {{ relinkError }}
          </div>
        </div>
      </div>

      <!-- Loading state (initial post-Run, before first poll response) -->
      <div v-else-if="loading" data-testid="job-loading" class="loading">
        Submitting…
      </div>

      <!-- Progress bar while running -->
      <div v-if="activeJob && activeJob.status === 'running'" data-testid="job-progress" class="job-progress">
        <div class="job-progress-phase">{{ activeJob.progress?.phase ?? 'running' }}</div>
        <div class="job-progress-bar">
          <div class="job-progress-fill"
               :style="{ width: Math.min(100, activeJob.progress?.percentComplete ?? 0) + '%' }" />
          <span class="job-progress-pct">
            {{ (activeJob.progress?.percentComplete ?? 0) > 100 ? '~' : '' }}{{ activeJob.progress?.percentComplete ?? 0 }}%
          </span>
        </div>
      </div>

      <!-- Strategy name banner -->
      <div v-if="currentResult" data-testid="strategy-banner" class="strategy-banner">
        {{ currentResult.strategyName }}
      </div>

      <!-- KPI cards -->
      <div v-if="currentResult" class="kpi-cards" :data-testid="`${assetId}-result-kpi-cards`">
        <div class="kpi-card">
          <div class="kpi-label">Sharpe</div>
          <div class="kpi-value" data-testid="kpi-sharpe">{{ currentResult.sharpeRatio?.toFixed(2) ?? '--' }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">CAGR</div>
          <div class="kpi-value" data-testid="kpi-cagr">{{ fmtPct(currentResult.cagrPct) }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">Max DD</div>
          <div class="kpi-value" data-testid="kpi-maxdd">{{ fmtPct(currentResult.maxDrawdownPct) }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">Hit rate</div>
          <div class="kpi-value" data-testid="kpi-hitrate">{{ currentResult.hitRatePct?.toFixed(1) ?? '--' }}%</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">Trades</div>
          <div class="kpi-value" data-testid="kpi-trades">{{ currentResult.tradeCount ?? '--' }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">Total return</div>
          <div class="kpi-value" data-testid="kpi-total-return">{{ fmtPct(currentResult.totalReturnPct) }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">B&amp;H return</div>
          <div class="kpi-value" data-testid="kpi-bh-return">{{ fmtPct(currentResult.buyAndHoldReturnPct) }}</div>
        </div>
      </div>

      <!-- Equity curve chart — verbatim copy of BGA's equity-chart SVG
           rendering, strategy + B&H only -->
      <div v-if="equityCurvePoints" class="equity-chart" data-testid="equity-chart">
        <div class="section-label">Equity Curve</div>
        <svg :viewBox="equityCurvePoints.viewBox" class="equity-svg" preserveAspectRatio="xMidYMid meet">
          <g v-html="equityCurvePoints.grid"></g>
          <!-- B&H line (dashed grey, behind strategy) -->
          <polyline
            v-if="equityCurvePoints.hasBh"
            :points="equityCurvePoints.bhPolyline"
            fill="none"
            stroke="#666"
            stroke-width="1"
            stroke-dasharray="4,3"
            stroke-linejoin="round"
          />
          <!-- Strategy line (solid green, on top) -->
          <polyline
            :points="equityCurvePoints.polyline"
            fill="none"
            stroke="#4caf50"
            stroke-width="1.5"
            stroke-linejoin="round"
          />
          <g v-html="equityCurvePoints.yLabels"></g>
          <g v-html="equityCurvePoints.xLabels"></g>
          <g v-html="equityCurvePoints.legend"></g>
        </svg>
      </div>
      <div v-else-if="currentResult" class="equity-placeholder">
        No equity curve data available
      </div>

      <p v-if="currentResult?.equityCurve?.length" class="npv-caption">
        Final NPV: {{ fmtGbp(currentResult.equityCurve[currentResult.equityCurve.length - 1]?.npv) }}
      </p>

      <!-- In-sample vs Walk-forward (refit) comparison bar (parity with BacktestPanel) -->
      <div v-if="currentJobType === 'walk-forward' && wfResult && (wfResult.inSampleResult?.equityCurve?.length || wfResult.refitResult?.equityCurve?.length)"
           class="comparison-bar" data-testid="wf-comparison-bar">
        <div class="comparison-side">
          <div class="comparison-label">IN-SAMPLE</div>
          <div class="comparison-metrics">
            <span>CAGR {{ fmtPct(wfResult.inSampleResult?.cagrPct) }}</span>
            <span>Sharpe {{ fmtSharpe(wfResult.inSampleResult?.sharpeRatio) }}</span>
          </div>
        </div>
        <div class="comparison-side">
          <div class="comparison-label">OUT-OF-SAMPLE (REFIT)</div>
          <div class="comparison-metrics">
            <span>CAGR {{ fmtPct(wfResult.refitResult?.cagrPct) }}</span>
            <span>Sharpe {{ fmtSharpe(wfResult.refitResult?.sharpeRatio) }}</span>
          </div>
          <div class="decay-row">
            <span class="decay-label">CAGR Decay:</span>
            <span :class="decayClass(wfResult.cagrDecayRefit)">{{ fmtPct(wfResult.cagrDecayRefit) }}</span>
            <span class="decay-label" style="margin-left: 8px;">Sharpe Decay:</span>
            <span :class="decayClass(wfResult.sharpeDecayRefit)">{{ fmtPct(wfResult.sharpeDecayRefit) }}</span>
          </div>
        </div>
      </div>

      <!-- Walk-forward fold table (only if we have fold RESULTS, not just fold periods) -->
      <div v-if="currentJobType === 'walk-forward' && hasFoldResults" class="fold-section" data-testid="wf-fold-table">
        <div class="section-label">Walk-Forward Folds</div>
        <table class="fold-table">
          <thead>
            <tr>
              <th>Fold</th>
              <th>Test Period</th>
              <th>Return</th>
              <th>Sharpe</th>
              <th>Train Sharpe</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="fold in currentFolds" :key="fold.fold">
              <td>{{ fold.fold }}</td>
              <td>{{ fold.testStart?.slice(0, 10) }} - {{ fold.testEnd?.slice(0, 10) }}</td>
              <td :class="valueClass(fold.result?.totalReturnPct)">
                {{ fmtPct(fold.result?.totalReturnPct) }}
              </td>
              <td :class="fold.result?.sharpeRatio != null && fold.result.sharpeRatio > 1 ? 'positive' : ''">
                {{ fmtSharpe(fold.result?.sharpeRatio) }}
              </td>
              <td>{{ fmtSharpe(fold.trainSharpe) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Walk-forward section — shared components verbatim from BGA's
           BacktestPanel binding paths (folds/meanOosScore/stdOosScore/scoreType
           on WalkForwardChart; topResults/scoreType + @load on
           OptimizerResultsTable, fed from the optional fixedGridResult
           sub-object of WalkForwardResultData). -->
      <div v-if="currentJobType === 'walk-forward' && wfOptimizeResult"
           data-testid="wf-section" class="wf-section">
        <h3>Walk-Forward Result</h3>
        <WalkForwardChart
          v-if="wfOptimizeResult.folds?.some(f => f.trainScore || f.testScore)"
          :folds="wfOptimizeResult.folds"
          :meanOosScore="wfOptimizeResult.meanOosScore ?? 0"
          :stdOosScore="wfOptimizeResult.stdOosScore ?? 0"
          :scoreType="wfOptimizeResult.scoreType ?? 'sharpe'"
        />
        <template v-if="wfOptimizeResult.fixedGridResult">
          <div class="opt-summary">
            Fixed Grid: {{ wfOptimizeResult.fixedGridResult.totalCombos.toLocaleString() }} combos in {{ (wfOptimizeResult.fixedGridResult.elapsedMs / 1000).toFixed(1) }}s
          </div>
          <OptimizerResultsTable
            :topResults="wfOptimizeResult.fixedGridResult.topResults"
            :scoreType="wfOptimizeResult.fixedGridResult.scoreType"
            @load="loadOptimizerRow"
          />
        </template>
      </div>

      <!-- CPCV section — mutually exclusive with wf-section above via
           currentJobType guard. Summary grid + fan chart + tables per
           BacktestPanel's cpcv-results block. -->
      <div v-if="currentJobType === 'cpcv' && activeJob.cpcvResult"
           :data-testid="`${assetId}-cpcv-distribution`" class="cpcv-section">
        <h3>CPCV Result</h3>
        <div class="summary-grid">
          <div><span class="label">N:</span> {{ activeJob.cpcvResult.n }}</div>
          <div><span class="label">k:</span> {{ activeJob.cpcvResult.k }}</div>
          <div><span class="label">Embargo:</span> {{ activeJob.cpcvResult.embargoDays }} days</div>
          <div><span class="label">Combinations:</span> {{ activeJob.cpcvResult.numCombinations }}</div>
          <div><span class="label">Paths:</span> {{ activeJob.cpcvResult.numPaths }}</div>
          <div><span class="label">Elapsed:</span> {{ (activeJob.cpcvResult.elapsedMs / 1000).toFixed(1) }}s</div>
        </div>

        <!-- Fan chart — shared component, identical to BGA/JH -->
        <CpcvPathsChart :cpcvResult="activeJob.cpcvResult" />

        <h4>Distribution Across Paths</h4>
        <table class="dist-table" data-testid="cpcv-dist-table">
          <thead><tr><th>Metric</th><th>Median</th><th>Mean</th><th>StdDev</th><th>p5</th><th>p95</th></tr></thead>
          <tbody>
            <tr v-for="[k, d] in Object.entries(distributionRows)" :key="k">
              <td>{{ k }}</td>
              <td>{{ typeof (d as any).median === 'number' ? (d as any).median.toFixed(3) : '—' }}</td>
              <td>{{ typeof (d as any).mean === 'number' ? (d as any).mean.toFixed(3) : '—' }}</td>
              <td :style="{ color: stdColour((d as any).stdDev, (d as any).mean) }">{{ typeof (d as any).stdDev === 'number' ? (d as any).stdDev.toFixed(3) : '—' }}</td>
              <td>{{ typeof (d as any).p5 === 'number' ? (d as any).p5.toFixed(3) : '—' }}</td>
              <td>{{ typeof (d as any).p95 === 'number' ? (d as any).p95.toFixed(3) : '—' }}</td>
            </tr>
          </tbody>
        </table>

        <h4>Per-Path Metrics</h4>
        <table class="path-table" data-testid="cpcv-path-table">
          <thead><tr><th>Path</th><th>Sharpe</th><th>Sortino</th><th>CAGR</th><th>Max DD</th><th>Trades</th><th>Win rate</th></tr></thead>
          <tbody>
            <tr v-for="p in activeJob.cpcvResult.paths" :key="p.pathId">
              <td><span class="dot" :style="{ background: pathConsistencyColour(p.sharpeRatio) }"></span>{{ p.pathId }}</td>
              <td>{{ p.sharpeRatio?.toFixed(2) ?? '—' }}</td>
              <td>{{ p.sortinoRatio?.toFixed(2) ?? '—' }}</td>
              <td>{{ fmtPct(p.cagrPct) }}</td>
              <td>{{ fmtPct(p.maxDrawdownPct) }}</td>
              <td>{{ p.tradeCount ?? '—' }}</td>
              <td>{{ fmtPct(p.winRate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- TPE Full Optimize results (parity with BacktestPanel) -->
      <template v-if="tpeResult">
        <div class="opt-summary" data-testid="tpe-summary">
          TPE: {{ tpeResult.totalTrials }} trials across {{ tpeResult.groups.length }} strategy families
          in {{ (tpeResult.elapsedMs / 1000).toFixed(1) }}s
        </div>
        <div class="tpe-groups">
          <table class="tpe-table" data-testid="tpe-table">
            <thead>
              <tr>
                <th></th>
                <th>Strategy</th>
                <th>Sharpe</th>
                <th>CAGR</th>
                <th>Max DD</th>
                <th>Trades</th>
                <th>DSR</th>
                <th>Trials</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <!-- Metric columns prefer fullResult when the detail pass has
                   run for the group (authoritative full-engine numbers);
                   g.* fields are the lightweight-runner approximations. -->
              <tr v-for="(g, idx) in tpeResult.groups" :key="g.groupKey"
                  class="tpe-group-row" :class="{ 'tpe-winner': idx === 0 }">
                <td>{{ idx === 0 ? '★' : '' }}</td>
                <td>{{ g.label }}</td>
                <td>{{ ((g.fullResult?.sharpeRatio ?? g.sharpe) ?? 0).toFixed(2) }}</td>
                <td>{{ (((g.fullResult?.cagrPct ?? g.cagr) ?? 0) * 100).toFixed(1) }}%</td>
                <td>{{ (((g.fullResult?.maxDrawdownPct ?? g.maxDrawdownPct) ?? 0) * 100).toFixed(1) }}%</td>
                <td>{{ g.fullResult?.tradeCount ?? g.tradeCount ?? 0 }}</td>
                <td>{{ ((g.fullResult?.deflatedSharpeRatio ?? g.deflatedSharpe) ?? 0).toFixed(2) }}</td>
                <td class="pill-dim">{{ g.trialCount }}</td>
                <td>
                  <button class="btn-sm" @click="loadTpeWinner(g)"
                    :disabled="tpeGroupBackfilling !== null"
                    :title="!g.fullResult ? 'Group is outside the top-10 detail pass — clicking Load will run a fresh single-mode backtest with these params (~15-30s) so the KPI tiles and equity curve reflect this group.' : ''">
                    {{ tpeGroupBackfilling?.label === g.label ? 'Running…' : 'Load' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
    </div>
  </div>
</template>

<style scoped>
.fund-backtest { padding: 1rem; color: #ccc; }
.fund-backtest h2 { color: #eee; margin: 0 0 1rem; font-size: 1rem; }
.fund-backtest h3 { color: #aaa; font-size: 0.85rem; margin: 1rem 0 0.5rem; text-transform: uppercase; letter-spacing: 1px; }
section { margin-bottom: 1rem; padding: 0.75rem; background: #1a1a1a; border: 1px solid #333; border-radius: 4px; }
label { display: block; margin: 0.35rem 0; font-size: 0.85rem; }
select, input[type="number"], input[type="text"], .config-save-row input { background: #111; color: #ccc; border: 1px solid #444; border-radius: 3px; padding: 3px 6px; margin-left: 6px; }
.error { color: #f87171; padding: 0.5rem; margin-bottom: 0.75rem; background: #2a1010; border: 1px solid #7f1d1d; border-radius: 4px; }
.live-applied-banner { color: #4caf50; padding: 0.5rem; margin-bottom: 0.75rem; background: #102a10; border: 1px solid #1d7f1d; border-radius: 4px; font-size: 0.85rem; }
.job-id { color: #888; font-family: monospace; font-size: 0.85rem; margin-top: 0.75rem; }

.panel-grid { display: grid; grid-template-columns: minmax(340px, 1fr) 2fr; gap: 1rem; }
@media (max-width: 900px) { .panel-grid { grid-template-columns: 1fr; } }
.controls-column, .results-column { min-width: 0; }

/* Config CRUD row — same classes/visuals as BacktestPanel */
.config-btn-row { display: flex; gap: 6px; margin-top: 0.5rem; }
.config-save-row { display: flex; gap: 6px; margin-top: 0.5rem; align-items: center; }
.config-save-row input { flex: 1; margin-left: 0; }
.btn-sm { background: #333; border: 1px solid #444; color: #ccc; padding: 4px 10px; border-radius: 4px; font-size: 11px; cursor: pointer; flex: 1; }
.btn-sm:hover { background: #444; }
.btn-sm:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-sm.btn-danger { background: #3a1a1a; border-color: #5a2a2a; color: #f88; }

/* Run buttons — same visual language as BacktestPanel */
.btn-run { display: block; width: 100%; padding: 0.6rem 1rem; border-radius: 4px; border: none; cursor: pointer; font-weight: 600; font-size: 0.9rem; }
.btn-run:disabled { opacity: 0.6; cursor: default; }
.primary-run-btn { background: #2e7d32; color: #fff; }
.primary-run-btn:hover:not(:disabled) { background: #388e3c; }
.btn-tpe { background: #7b1fa2; color: #fff; width: auto; display: inline-block; }
.btn-tpe:hover:not(:disabled) { background: #8e24aa; }

.run-row { display: flex; gap: 8px; align-items: stretch; }
.btn-cancel {
  padding: 0.6rem 1rem; background: #b3261e; color: #fff; border: none;
  border-radius: 4px; cursor: pointer; font-weight: 600; font-size: 0.9rem;
  flex: 0 0 auto;
}
.btn-cancel:hover { background: #d32f2f; }
.run-preview { font-size: 0.72rem; color: #888; text-align: center; margin-top: 0.4rem; font-family: monospace; }
.stack-preview { color: #b8860b; }
.advanced-toggle { color: #aaa; font-size: 0.8rem; margin-top: 0.75rem; cursor: pointer; user-select: none; }
.advanced-runs { display: flex; align-items: center; gap: 8px; margin-top: 0.5rem; }
.tpe-budget-input { width: 70px; }

.btn-action { background: #263238; border: 1px solid #37474f; color: #cfd8dc; padding: 6px 14px; border-radius: 4px; font-size: 12px; cursor: pointer; }
.btn-action:hover:not(:disabled) { background: #37474f; }
.btn-action:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-action.btn-warn { background: #3a2a10; border-color: #5a4a20; color: #fb9; }
.apply-btn { display: block; width: 100%; margin-top: 0.75rem; }
.apply-caveat { font-size: 0.68rem; color: #776; margin-top: 0.35rem; line-height: 1.35; }

.results-header { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem; }
.job-type-badge { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 1px; color: #888; background: #1a1a1a; border: 1px solid #333; border-radius: 3px; padding: 2px 8px; }

.results-action-bar { display: flex; gap: 8px; margin-bottom: 0.75rem; }

.run-summary { background: #14181c; border: 1px solid #2a2f36; border-radius: 4px; padding: 0.5rem 0.75rem; margin-bottom: 0.75rem; }
.run-summary-top { font-size: 0.85rem; color: #ddd; }
.run-summary-top .run-type { font-weight: 600; color: #4caf50; }
.run-summary-top .run-preset { color: #999; }
.run-summary-meta { font-size: 0.72rem; color: #778; font-family: monospace; margin-top: 2px; }

.no-results, .loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #555;
  font-style: italic;
  font-size: 0.85rem;
  text-align: center;
}

.job-progress { margin-bottom: 1rem; padding: 0.75rem; background: #1a2a1a; border: 1px solid #2a4a2a; border-radius: 4px; }
.job-progress-phase { font-size: 0.75rem; color: #4caf50; margin-bottom: 0.35rem; }
.job-progress-bar { position: relative; background: #333; border-radius: 2px; height: 14px; overflow: hidden; }
.job-progress-fill { background: #4caf50; height: 100%; border-radius: 2px; transition: width 0.3s; }
.job-progress-pct {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.65rem; color: #eee; font-family: monospace;
}

.strategy-banner {
  background: linear-gradient(135deg, #1a2a1a, #0a0a0a);
  border: 1px solid #2a4a2a;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #4caf50;
  text-align: center;
  letter-spacing: 0.5px;
}

.kpi-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 0.75rem; }
.kpi-card { background: #1a1a1a; border: 1px solid #333; border-radius: 4px; padding: 0.75rem; text-align: center; }
.kpi-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px; color: #888; margin-bottom: 0.25rem; }
.kpi-value { font-size: 1.25rem; color: #eee; font-weight: 600; }
.npv-caption { color: #888; font-size: 0.8rem; margin-top: 0.75rem; }


.section-label {
  font-size: 10px;
  color: #666;
  text-transform: uppercase;
  margin: 10px 0 6px;
  letter-spacing: 0.5px;
}
.equity-chart {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 12px;
  margin-top: 0.75rem;
}
.equity-svg {
  width: 100%;
  height: auto;
  min-height: 120px;
  max-height: 200px;
}
.equity-placeholder {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 24px;
  text-align: center;
  color: #555;
  font-size: 12px;
  font-style: italic;
  margin-top: 0.75rem;
}

/* Walk-forward comparison bar + fold table + decay — same visuals as BacktestPanel */
.comparison-bar { display: flex; gap: 1rem; margin-top: 0.75rem; }
.comparison-side { flex: 1; background: #1a1a1a; border: 1px solid #333; border-radius: 4px; padding: 0.6rem 0.75rem; }
.comparison-label { font-size: 0.65rem; color: #888; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 0.3rem; }
.comparison-metrics { display: flex; gap: 12px; font-size: 0.85rem; color: #ddd; font-family: monospace; }
.decay-row { margin-top: 0.4rem; font-size: 0.75rem; font-family: monospace; }
.decay-label { color: #888; }
.decay-warn { color: #fbc02d; }
.decay-bad { color: #f44336; }
.fold-section { margin-top: 0.75rem; background: #1a1a1a; border: 1px solid #333; border-radius: 4px; padding: 12px; }
.fold-table { width: 100%; border-collapse: collapse; font-family: 'JetBrains Mono', monospace; font-size: 0.8rem; }
.fold-table th, .fold-table td { padding: 0.35rem 0.5rem; text-align: right; border-bottom: 1px solid #333; }
.fold-table th:first-child, .fold-table td:first-child,
.fold-table th:nth-child(2), .fold-table td:nth-child(2) { text-align: left; }
.fold-table th { color: #888; font-weight: normal; }
.positive { color: #4caf50; }
.negative { color: #f44336; }

.wf-section {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 12px;
  margin-top: 0.75rem;
}
.opt-summary { color: #888; font-size: 0.8rem; margin: 0.5rem 0; }

.cpcv-section {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 12px;
  margin-top: 0.75rem;
}
.cpcv-section h4 { margin: 0.75rem 0 0.5rem; font-size: 0.85rem; color: #aaa; font-weight: normal; text-transform: none; letter-spacing: normal; }
.summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px 16px; font-size: 0.8rem; color: #ccc; font-family: monospace; margin-bottom: 0.5rem; }
.summary-grid .label { color: #888; }
.dist-table, .path-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
}
.dist-table th, .dist-table td, .path-table th, .path-table td {
  padding: 0.4rem 0.6rem;
  text-align: right;
  border-bottom: 1px solid #333;
}
.dist-table th:first-child, .dist-table td:first-child,
.path-table th:first-child, .path-table td:first-child { text-align: left; }
.dist-table th, .path-table th { color: #888; font-weight: normal; }
.dist-table .dot, .path-table .dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.4rem;
}

/* TPE leaderboard — same structure as BacktestPanel's mit-table */
.tpe-groups { margin-top: 0.5rem; }
.tpe-table { width: 100%; border-collapse: collapse; font-family: 'JetBrains Mono', monospace; font-size: 0.8rem; }
.tpe-table th, .tpe-table td { padding: 0.35rem 0.5rem; text-align: right; border-bottom: 1px solid #333; }
.tpe-table th:nth-child(2), .tpe-table td:nth-child(2) { text-align: left; }
.tpe-table th { color: #888; font-weight: normal; }
.tpe-winner { background: #14231a; }
.pill-dim { color: #667; }
.tpe-table .btn-sm { flex: none; }
</style>
